const LOCAL_RELAY_SERVER_URL: string =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL ||
  'https://relay-server-763064197709.asia-south2.run.app';

import { useEffect, useRef, useCallback, useState, useContext } from 'react';

import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';

import { X, Zap, Mic, FileText } from 'react-feather';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';
import { ThemeToggle } from '../components/theme-toggle/ThemeToggle';
import { ThemeContext } from '../context/ThemeContext';

import './ConsolePage.scss';

export function ConsolePage() {
  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 }),
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 }),
  );
  const clientRef = useRef<RealtimeClient>(new RealtimeClient());
  // const clientRef = useRef<RealtimeClient>(
  //   new RealtimeClient({
  //     url: LOCAL_RELAY_SERVER_URL,
  //   }),
  // );

  const [items, setItems] = useState<ItemType[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [turnEndType, setTurnEndType] = useState('none');
  const [logoAnimated, setLogoAnimated] = useState(false);
  const [getStartedVisible, setGetStartedVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { theme } = useContext(ThemeContext);

  /**
   * Detect if the device is mobile
   */
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check initially
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.pause();
      client.createResponse();
    } else {
      // Start recording
      setIsRecording(true);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
  };

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // First animate the logo and hide Get Started text
    setLogoAnimated(true);
    setGetStartedVisible(false);

    // Wait for animation to complete before connecting
    setTimeout(async () => {
      setIsConnected(true);
      setItems(client.conversation.getItems());

      // Connect to microphone
      await wavRecorder.begin();

      // Connect to audio output
      await wavStreamPlayer.connect();

      // Connect to realtime API
      await client.connect();
      
      // Still send the Hello message for initialization, but we won't display it
      const initialMessageId = await client.sendUserMessageContent([
        {
          type: `input_text`,
          text: `Hello!`,
        },
      ]);

      if (client.getTurnDetectionType() === 'server_vad') {
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      }
    }, 1000); // Wait for the animation to complete
  }, []);

  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);
    setLogoAnimated(false);
    setGetStartedVisible(true);
    setItems([]);

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();
  }, []);

  const deleteConversationItem = useCallback(async (id: string) => {
    const client = clientRef.current;
    client.deleteItem(id);
  }, []);

  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const trackSampleOffset = await wavStreamPlayer.interrupt();
    if (trackSampleOffset?.trackId) {
      const { trackId, offset } = trackSampleOffset;
      await client.cancelResponse(trackId, offset);
    }
    await wavRecorder.record((data) => client.appendInputAudio(data.mono));
  };

  const stopRecording = async () => {
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();
    client.createResponse();
  };

  const changeTurnEndType = async (value: string) => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;

    setTurnEndType(value);

    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
    setCanPushToTalk(value === 'none');
  };

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]'),
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      
      // Scroll to the latest conversation item if it exists
      const latestConversationItem = conversationEl.querySelector('.latest-conversation:last-of-type');
      if (latestConversationItem) {
        // Smooth scroll to the latest conversation
        latestConversationItem.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      } else {
        // If no latest conversation item, scroll to bottom as before
        conversationEl.scrollTop = conversationEl.scrollHeight;
      }
    }

    // Add this console log to see the entire conversation context
    console.log('Conversation Items:', items);
  }, [items]);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000,
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
    };
  }, []);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage" data-theme={theme} className="full-screen">
      {/* Top Bar with Ojuss.app text and theme toggle */}
      <div className="top-bar">
        <div className="app-title">Ojuss.app</div>
        <div className="theme-toggle-container">
          <ThemeToggle />
        </div>
      </div>

      <div className="content-main">
        <div className="content-logs">
          <div className={`content-block conversation ${isConnected ? 'active' : ''}`}>
            <div className="content-block-title">conversation</div>
            <div className="content-block-body" data-conversation-content>
              {!items.length && (
                <div className="initial-message">
                  
                </div>
              )}
              {items
                // Filter out the initial Hello message 
                .filter(item => !(item.role === 'user' && 
                                  item.formatted.text === 'Hello!' && 
                                  items.findIndex(i => i.id === item.id) === 0))
                .map((conversationItem, index, filteredArray) => {
                  // Check if this is the latest conversation (last user message and last assistant message)
                  const isLatestUserMessage = conversationItem.role === 'user' && index === filteredArray.length - 2;
                  const isLatestAssistantMessage = conversationItem.role === 'assistant' && index === filteredArray.length - 1;
                  const isLatestConversation = isLatestUserMessage || isLatestAssistantMessage;
                  
                  // Check if this is a consecutive message (same role as previous message)
                  const isPreviousSameRole = index > 0 && filteredArray[index - 1].role === conversationItem.role;
                  
                  return (
                  <div 
                    className={`conversation-item ${isLatestConversation ? 'latest-conversation' : ''} ${isPreviousSameRole ? 'consecutive-message' : ''}`} 
                    key={conversationItem.id}
                  >
                    <div className={`message-container ${conversationItem.role || ''}`}>
                      <div className="message-content">
                        {/* tool response */}
                        {conversationItem.type === 'function_call_output' && (
                          <div>{conversationItem.formatted.output}</div>
                        )}
                        {/* tool call */}
                        {!!conversationItem.formatted.tool && (
                          <div>
                            {conversationItem.formatted.tool.name}(
                            {conversationItem.formatted.tool.arguments})
                          </div>
                        )}
                        {!conversationItem.formatted.tool &&
                          conversationItem.role === 'user' && (
                            <div>
                              {conversationItem.formatted.transcript ||
                                (conversationItem.formatted.audio?.length
                                  ? '(awaiting transcript)'
                                  : conversationItem.formatted.text ||
                                  '(item sent)')}
                            </div>
                          )}
                        {!conversationItem.formatted.tool &&
                          conversationItem.role === 'assistant' && (
                            <div>
                              {conversationItem.formatted.transcript ||
                                conversationItem.formatted.text ||
                                '(interrupted)'}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )})}
            </div>
          </div>
        </div>
      </div>

      {/* Centered Logo */}
      <div className={`logo-container ${logoAnimated ? 'animated' : ''} ${isConnected ? 'connected' : ''} ${turnEndType === 'server_vad' ? 'vad-mode' : ''}`}>
        {!isConnected ? (
          <div className="logo-wrapper">
            <img
              src="/logomark.svg"
              alt="Ojuss Logo"
              onClick={connectConversation}
              className="logo-image"
            />
            {getStartedVisible && (
              <div className="get-started-text">Get Started</div>
            )}
          </div>
        ) : (
          <div
            className={`logo-push-to-talk ${isRecording ? 'recording' : ''}`}
            onClick={isMobile && canPushToTalk ? toggleRecording : undefined}
            onMouseDown={!isMobile && canPushToTalk ? startRecording : undefined}
            onMouseUp={!isMobile && canPushToTalk ? stopRecording : undefined}
            onTouchStart={!isMobile && canPushToTalk ? startRecording : undefined}
            onTouchEnd={!isMobile && canPushToTalk ? stopRecording : undefined}
          >
            <img
              src="/logomark.svg"
              alt="Ojuss Logo"
              className={`logo-image ${!canPushToTalk ? 'disabled' : ''} ${isRecording ? 'recording' : ''}`}
            />
            {/* {isMobile && canPushToTalk && (
              <div className="recording-status">
                {isRecording ? 'Tap to Stop' : 'Tap to Record'}
              </div>
            )} */}
          </div>
        )}
      </div>

      {/* Floating Input Mode Toggle */}
      {isConnected && (
        <div className="floating-controls">
          <Button
            label={turnEndType === 'none' ? 'Manual' : 'Voice'}
            icon={turnEndType === 'none' ? FileText : Mic}
            buttonStyle={turnEndType === 'none' ? 'regular' : 'action'}
            onClick={() => changeTurnEndType(turnEndType === 'none' ? 'server_vad' : 'none')}
            aria-label={turnEndType === 'none' ? 'Switch to Voice Activation' : 'Switch to Manual Mode'}
            iconOnly
          />
        </div>
      )}
      
      {/* Disconnect Button in Bottom Left */}
      {isConnected && (
        <div className="disconnect-button-container">
          <Button
            label="Disconnect"
            icon={X}
            buttonStyle="alert"
            onClick={disconnectConversation}
            aria-label="Disconnect"
            iconOnly
          />
        </div>
      )}
    </div>
  );
}
